import React from 'react';
import { ComplianceCompanyConfig } from '@/constants/compliance-companies';
import { Button } from '@/components/ui/button';
import { Card } from '@/components/ui/card';
import { Shield, Clock, Target, Users } from 'lucide-react';

interface ComplianceLandingProps {
  config: ComplianceCompanyConfig;
  onStartTraining: () => void;
}

export const ComplianceLanding: React.FC<ComplianceLandingProps> = ({ config, onStartTraining }) => {
  return (
    <div className="min-h-screen bg-gradient-to-br from-gray-50 to-gray-100">
      {/* Header */}
      <div className="bg-white border-b border-gray-200">
        <div className="max-w-7xl mx-auto px-6 py-4 flex items-center justify-between">
          <img src={config.logo} alt={config.name} className="h-10" />
          <div className="flex items-center gap-2 text-sm text-gray-600">
            <span>Powered by</span>
            <img src={config.hupoLogo} alt="Hupo" className="h-6" />
          </div>
        </div>
      </div>

      {/* Main Content */}
      <div className="max-w-4xl mx-auto px-6 py-12">
        <div className="text-center mb-12">
          <h1 className="text-4xl font-bold text-gray-900 mb-4">
            {config.landing.headline}
          </h1>
          <p className="text-xl text-gray-600 mb-6">
            {config.landing.subheadline}
          </p>
          <p className="text-gray-600 max-w-2xl mx-auto">
            {config.landing.description}
          </p>
        </div>

        {/* Scenario Card */}
        <Card className="p-8 mb-8 bg-white shadow-lg">
          <h2 className="text-2xl font-semibold mb-6 text-gray-900">
            {config.scenario.title}
          </h2>

          <div className="space-y-6">
            <div>
              <h3 className="font-medium text-gray-700 mb-2 flex items-center">
                <Users className="w-4 h-4 mr-2" />
                Scenario Context
              </h3>
              <p className="text-gray-600">{config.scenario.context}</p>
            </div>

            <div className="grid md:grid-cols-2 gap-6">
              <div>
                <h4 className="font-medium text-gray-700 mb-2">Your Role</h4>
                <div className="bg-blue-50 p-4 rounded-lg">
                  <p className="font-semibold text-gray-900">{config.scenario.participants.user.name}</p>
                  <p className="text-sm text-gray-600">{config.scenario.participants.user.role}</p>
                  <p className="text-sm text-gray-500">{config.scenario.participants.user.location}</p>
                </div>
              </div>

              <div>
                <h4 className="font-medium text-gray-700 mb-2">Client</h4>
                <div className="bg-gray-50 p-4 rounded-lg">
                  <p className="font-semibold text-gray-900">{config.scenario.participants.client.name}</p>
                  <p className="text-sm text-gray-600">{config.scenario.participants.client.role}</p>
                  <p className="text-sm text-gray-500">{config.scenario.participants.client.company}</p>
                </div>
              </div>
            </div>

            <div>
              <h3 className="font-medium text-gray-700 mb-3 flex items-center">
                <Target className="w-4 h-4 mr-2" />
                Training Objectives
              </h3>
              <ul className="space-y-2">
                {config.scenario.objectives.map((objective, index) => (
                  <li key={index} className="flex items-start">
                    <Shield className="w-4 h-4 mr-2 mt-0.5 text-blue-600 flex-shrink-0" />
                    <span className="text-gray-600">{objective}</span>
                  </li>
                ))}
              </ul>
            </div>

            <div className="flex items-center justify-between pt-4 border-t">
              <div className="flex items-center text-gray-500">
                <Clock className="w-4 h-4 mr-2" />
                <span className="text-sm">Duration: {config.scenario.duration}</span>
              </div>

              <Button
                onClick={onStartTraining}
                className="px-8 py-3 text-lg font-semibold"
                style={{
                  backgroundColor: config.colors.primary,
                  color: 'white'
                }}
              >
                {config.landing.ctaText}
              </Button>
            </div>
          </div>
        </Card>
      </div>
    </div>
  );
};